// Tracciabilità della registrazione alle notifiche push (sezione "FE.notifiche").
// In `note` la prima voce è `esito`: lo stesso messaggio breve che vede l'utente (PUSH_MESSAGES).
// Seguono `verdetto` (OK, KO, IN ATTESA), `dettaglio` (la spiegazione per chi legge il log) e lo stato
// del permesso e della registrazione.
// Nessuna funzione di questo file lancia errori: la tracciabilità non deve disturbare l'app.
import { trace } from "./traceability";
import { APP_VERSION } from "./version";

const SEZIONE = "FE.notifiche";

// Sotto questa durata la risposta è molto rapida: si scrive il dato osservato, senza attribuire la decisione.
const BROWSER_ANSWER_MS = 500;

const traced = new WeakSet();

function isInstalledApp() {
  try {
    return (
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      navigator.standalone === true
    );
  } catch (_) {
    return false;
  }
}

function isIos() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

// Permesso notifiche del browser per questo sito: vale per tutti gli utenti che usano questo browser.
function currentPermission() {
  try {
    return typeof Notification === "undefined" ? "non_disponibile" : Notification.permission;
  } catch (_) {
    return "non_disponibile";
  }
}

// "Chrome 155 · Windows", "Safari 18 · iOS 18.3", "Chrome 128 · Android 14 (SM-S911B)", "App installata · iOS 18.3".
// Se non riconosce browser o sistema ritorna anche lo user agent grezzo (serve a capire).
function describeDevice() {
  const ua = navigator.userAgent || "";
  let os = null;
  let m;
  if ((m = ua.match(/(?:iPhone|CPU) OS (\d+)_(\d+)/))) os = `iOS ${m[1]}.${m[2]}`;
  else if ((m = ua.match(/Android (\d+)(?:; ([^;)]+))?/))) os = `Android ${m[1]}${m[2] ? ` (${m[2].trim()})` : ""}`;
  else if (/Windows NT/.test(ua)) os = "Windows";
  else if (/Mac OS X/.test(ua)) os = "macOS";
  else if (/Linux|X11/.test(ua)) os = "Linux";

  let browser = null;
  const pick = (re, name) => {
    const r = ua.match(re);
    return r ? `${name} ${r[1]}` : null;
  };
  browser =
    pick(/EdgA?\/(\d+)/, "Edge") ||
    pick(/OPR\/(\d+)/, "Opera") ||
    pick(/SamsungBrowser\/(\d+)/, "Samsung Internet") ||
    pick(/(?:CriOS|Chrome)\/(\d+)/, "Chrome") ||
    pick(/(?:FxiOS|Firefox)\/(\d+)/, "Firefox") ||
    (/Safari\//.test(ua) ? pick(/Version\/(\d+)/, "Safari") : null);

  if (isInstalledApp()) {
    // "App installata · iOS 18.3 (Safari 18)": dall'icona sulla Home il browser spesso non si riconosce, e non serve.
    return {
      dispositivo: `App installata · ${os || "sistema sconosciuto"}${browser ? ` (${browser})` : ""}`,
      ...(os ? {} : { user_agent: ua }),
    };
  }
  const inApp = /; wv\)|FBAN|FBAV|Instagram|Snapchat|Line\//.test(ua) ? " (browser interno di un'app)" : "";
  const recognized = Boolean(os && browser);
  return {
    dispositivo: `${browser || "browser sconosciuto"}${inApp} · ${os || "sistema sconosciuto"}`,
    ...(recognized ? {} : { user_agent: ua }),
  };
}

// Stato al momento della riga: permesso del browser e cosa risulta registrato su Cedolino per l'utente.
// Se `extra` porta già un valore più aggiornato (es. dalla verifica appena fatta), vale quello.
function baseNote(user, extra) {
  const state = { permesso: currentPermission() };
  if (typeof user?.token_notifiche_reg === "boolean") state.registrato_server = user.token_notifiche_reg;
  if (typeof user?.token_notifiche_has_any === "boolean") state.altri_dispositivi = user.token_notifiche_has_any;
  Object.keys(extra).forEach((key) => delete state[key]);
  return {
    ...extra,
    ...state,
    ...describeDevice(),
    app_installata: isInstalledApp(),
    app_version: APP_VERSION,
  };
}

// Quale funzione del browser manca (serve solo al log: il controllo originale resta in App.js).
export function getMissingPushApi() {
  if (!("serviceWorker" in navigator)) return "no_service_worker";
  if (!("PushManager" in window)) return "no_push_manager";
  if (!("Notification" in window)) return "no_notification";
  return null;
}

// Messaggi per l'utente: brevi, con la soluzione. Sono gli stessi che finiscono in `esito`.
export const PUSH_MESSAGES = {
  ok: "Dispositivo registrato correttamente.",
  bloccateDispositivo: "Notifiche bloccate: abilitale dalle impostazioni del dispositivo",
  bloccateBrowser: "Notifiche bloccate: abilitale dalle impostazioni del browser",
  soloDaApp: "Notifiche solo dall'app: aggiungila alla schermata Home",
  aggiornaIos: "Notifiche non supportate: aggiorna iOS",
  browserNonSupportato: "Browser non supportato: usa Chrome o Safari",
  dispositivoNonSupportato: "Notifiche non supportate su questo dispositivo",
  permessoNonConcesso: "Permesso non concesso: tocca Registra e scegli Consenti",
  sessioneScaduta: "Sessione scaduta: rifai l'accesso",
  riprova: "Registrazione non riuscita: riprova più tardi",
  rimandata: "Registrazione rimandata dall'utente (Non ora)",
  swLento: "Registrazione in attesa: app non ancora pronta",
};

function blockedMessage() {
  return isInstalledApp() ? PUSH_MESSAGES.bloccateDispositivo : PUSH_MESSAGES.bloccateBrowser;
}

function unsupportedMessage(causa) {
  if (causa === "no_service_worker") return PUSH_MESSAGES.browserNonSupportato;
  if (isIos()) return isInstalledApp() ? PUSH_MESSAGES.aggiornaIos : PUSH_MESSAGES.soloDaApp;
  return isInstalledApp() ? PUSH_MESSAGES.dispositivoNonSupportato : PUSH_MESSAGES.browserNonSupportato;
}

// Spiegazione per chi legge il log (`dettaglio`): parla di browser o di app installata a seconda di come
// l'utente ha aperto EasyJob. Con l'app installata non c'è lucchetto né indirizzo.
function context() {
  const ua = navigator.userAgent || "";
  if (!isInstalledApp()) {
    return {
      soggetto: "il browser",
      bloccate:
        "le notifiche sono bloccate per questo sito nelle impostazioni del browser (vale per tutti gli utenti che usano questo browser); si sbloccano dal lucchetto accanto all'indirizzo",
    };
  }
  const sblocco = isIos()
    ? "si sbloccano da Impostazioni dell'iPhone → Notifiche → EasyJob"
    : /Android/.test(ua)
      ? "si sbloccano tenendo premuta l'icona di EasyJob → Info app → Notifiche"
      : "si sbloccano dalle impostazioni del sito nel menu dell'app installata";
  return {
    soggetto: "il telefono",
    bloccate: `le notifiche sono bloccate per l'app EasyJob nelle impostazioni del telefono (vale per tutti gli utenti che usano l'app su questo telefono); ${sblocco}`,
  };
}

const capitalize = (text) => text.charAt(0).toUpperCase() + text.slice(1);

function unsupportedDetail(causa) {
  if (causa === "no_service_worker") {
    return "il browser non permette di usare l'app (nessun service worker): impossibile registrarsi alle notifiche";
  }
  if (isIos()) {
    return isInstalledApp()
      ? "l'iPhone non supporta le notifiche per le app installate (serve iOS 16.4 o successivo)"
      : "iPhone con l'app aperta dal browser e non dall'icona sulla Home: le notifiche push non sono disponibili";
  }
  return isInstalledApp() ? "il telefono non supporta le notifiche push per l'app" : "il browser non supporta le notifiche push";
}

function permissionDenied({ permessoIniziale, permessoRisposta, durataMs, modalita }) {
  const { soggetto, bloccate } = context();
  const auto = modalita === "automatica" ? " La richiesta è partita in automatico al login." : "";
  if (permessoIniziale === "denied") {
    return { esito: blockedMessage(), dettaglio: `Permesso negato senza chiedere niente all'utente: ${bloccate}.` };
  }
  if (permessoRisposta === "default") {
    return {
      esito: PUSH_MESSAGES.permessoNonConcesso,
      dettaglio: `L'utente ha chiuso la richiesta di permesso notifiche senza rispondere: il permesso resta da chiedere.${auto}`,
    };
  }
  if (typeof durataMs === "number" && durataMs < BROWSER_ANSWER_MS) {
    return {
      esito: blockedMessage(),
      dettaglio: `Richiesta di permesso respinta in ${durataMs} ms: non si può stabilire se l'abbia decisa l'utente o ${soggetto}. Da ora ${bloccate}.${auto}`,
    };
  }
  return {
    esito: blockedMessage(),
    dettaglio: `L'utente ha negato la richiesta di permesso notifiche (Blocca / Non consentire): da ora ${bloccate}.${auto}`,
  };
}

// Per ogni causa: `esito` = il messaggio che vede l'utente, `dettaglio` = la spiegazione per chi legge il log.
function describeError(causa, { error, ...permissionInfo }) {
  const errore = error ? `: ${error.message || String(error)}` : "";
  switch (causa) {
    case "no_service_worker":
    case "no_push_manager":
    case "no_notification":
      return { esito: unsupportedMessage(causa), dettaglio: `Impossibile registrarsi: ${unsupportedDetail(causa)}` };
    case "sw_not_ready":
      return {
        esito: PUSH_MESSAGES.swLento,
        dettaglio: "Il service worker non è pronto dopo 10 secondi: la registrazione resta in attesa (non mostrato all'utente)",
      };
    case "permission_denied":
      return permissionDenied(permissionInfo);
    case "subscribe_failed":
      return {
        esito: PUSH_MESSAGES.riprova,
        dettaglio: `${capitalize(context().soggetto)} ha rifiutato l'iscrizione alle notifiche push${errore}`,
      };
    case "backend_failed":
      return { esito: PUSH_MESSAGES.riprova, dettaglio: `Il server non ha registrato il dispositivo${errore}` };
    case "no_session_token":
      return {
        esito: PUSH_MESSAGES.sessioneScaduta,
        dettaglio: "Sessione senza token: la registrazione non parte finché l'utente non rifà il login",
      };
    default:
      return { esito: PUSH_MESSAGES.riprova, dettaglio: `Errore non previsto durante la registrazione${errore}` };
  }
}

// Segna un errore già scritto nel registro, perché il punto di chiamata non lo riscriva come imprevisto.
export function markTraced(err) {
  try {
    if (err && typeof err === "object") traced.add(err);
  } catch (_) {
    // ignorato di proposito
  }
  return err;
}

// Scrive la riga e ritorna il messaggio per l'utente, così quello che vede e quello che c'è nel log coincidono.
// Ogni clic dell'utente (modalità "manuale") scrive sempre una riga; i tentativi automatici o
// silenziosi con la stessa causa si scrivono una volta per sessione, per non riempire la tabella.
export function tracePushError(
  user,
  causa,
  { passo, modalita, error, permessoIniziale, permessoRisposta, durataMs } = {}
) {
  let esito = PUSH_MESSAGES.riprova;
  try {
    const described = describeError(causa, { error, permessoIniziale, permessoRisposta, durataMs, modalita });
    esito = described.esito;
    trace({
      user,
      sezione: SEZIONE,
      azione: "errore_registrazione",
      note: baseNote(user, {
        esito,
        verdetto: "KO",
        dettaglio: described.dettaglio,
        causa,
        passo,
        modalita,
        ...(permessoIniziale ? { permesso_iniziale: permessoIniziale } : {}),
        ...(permessoRisposta ? { risposta_permesso: permessoRisposta } : {}),
        ...(typeof durataMs === "number" ? { durata_richiesta_ms: durataMs } : {}),
        ...(error ? { messaggio: error.message || String(error) } : {}),
      }),
      dedupeKey: modalita === "manuale" ? undefined : `${causa}:${passo}`,
    });
  } catch (_) {
    // ignorato di proposito
  }
  return esito;
}

// Errori che arrivano al punto di chiamata senza essere già stati scritti nel registro.
// Ritorna il messaggio per l'utente, o null se l'errore era già stato scritto (il suo messaggio è già quello giusto).
export function tracePushUnexpected(user, error, modalita) {
  if (error && typeof error === "object" && traced.has(error)) return null;
  return tracePushError(user, "errore_imprevisto", { passo: "registrazione", modalita, error });
}

export function tracePushConfirm(user, { modalita } = {}) {
  try {
    trace({
      user,
      sezione: SEZIONE,
      azione: "conferma_registrazione",
      note: baseNote(user, { esito: PUSH_MESSAGES.ok, verdetto: "OK", modalita }),
    });
  } catch (_) {
    // ignorato di proposito
  }
}

export function tracePushRefusal(user) {
  try {
    trace({
      user,
      sezione: SEZIONE,
      azione: "rifiuto_registrazione",
      note: baseNote(user, {
        esito: PUSH_MESSAGES.rimandata,
        verdetto: "IN ATTESA",
        dettaglio: "L'utente ha risposto Non ora alla domanda dell'app: il permesso notifiche non è stato chiesto",
      }),
      dedupeKey: "prompt",
    });
  } catch (_) {
    // ignorato di proposito
  }
}
